import { AeppicInterface } from '../aeppic.js'
import { Query } from '../../model'

type LocalStorageLike = {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

function getLocalStorage(): LocalStorageLike | null {
  try {
    const localStorage = globalThis.localStorage

    if (!localStorage || typeof localStorage.getItem !== 'function' || typeof localStorage.setItem !== 'function') {
      return null
    }

    return localStorage
  } catch (error) {
    return null
  }
}

export type Policy = {
  name: string
  reason: string
  queries: (Query.Query|Query.QueryBuilder)[],
  refresh: 'onboot'
  strategy: 'stale-subscriptions'
  evict: 'never'
}


export class Cache {
  private _policies: PolicyList = new PolicyList()

  constructor(private _aeppic: AeppicInterface) {    
  }

  addPolicy(policy: Policy) {
    const cachedPolicy: CachedPolicy =  {
      ...policy,
      queries: policy.queries.map(q => Query.toQuery(q)),
    }
    this._policies.addPolicy(cachedPolicy)
  }

  clear() {
    this._policies.clear()
  }
}

type CachedPolicy = {
  name: string
  reason: string
  queries: Query.Query[],
  refresh: 'onboot'
  strategy: 'stale-subscriptions'
  evict: 'never'
}

class PolicyList {
  private _policies: {
    [key: string]: CachedPolicy
  } = {}

  constructor() {
    this._restoreFromLocalStorage()
  }

  clear() {
    this._policies = {}
    this._storeInLocalStorage()
  }

  addPolicy(policy: CachedPolicy) {
    this._policies[policy.name] = policy
    this._storeInLocalStorage()
  }

  private _storeInLocalStorage() {
    const localStorage = getLocalStorage()

    if (!localStorage) {
      // console.warn('No local storage available, cannot store cache policies');
      return;
    }

    try {
      localStorage.setItem('policies', JSON.stringify(this._policies))
    } catch (error) {
      // Local storage is optional. Treat quota/security/runtime failures as unavailable.
    }
  }

  private _restoreFromLocalStorage() {
    const localStorage = getLocalStorage()

    if (!localStorage) {
      // console.warn('No local storage available, cannot restore cache policies')
      return;
    }

    const policies = localStorage.getItem('policies')

    if (policies) {
      try {
        this._policies = JSON.parse(policies)
      } catch (error) {
        console.error('Error parsing cache policies from local storage', error)
      }
    }
  }
}
